import {useState} from 'react'

import {HierarchyTableNode} from '../types'
import {
  ExpertSystemUsageLocationHistory,
  findPathToNode,
  resolveNavigationFromHistory
} from '../utils'
import {isKilnOrMillLevel} from '../utils/hierarchy.utils'

export const useExpertSystemUsageListNavigation = (rootNodes: HierarchyTableNode[]) => {
  const [locationHistory, setLocationHistory] = useState<ExpertSystemUsageLocationHistory[]>([])
  const [locationSiblings, setLocationSiblings] = useState<ExpertSystemUsageLocationHistory[]>([])

  const getCurrentLocations = (): HierarchyTableNode[] => {
    return locationHistory.reduce<HierarchyTableNode | HierarchyTableNode[]>(
      (current, historyItem) => {
        if (!current) return rootNodes
        if (Array.isArray(current)) {
          return current.find((node) => node.id === historyItem.id)?.subItems || []
        }
        return current.subItems?.find((node) => node.id === historyItem.id)?.subItems || []
      },
      rootNodes
    ) as HierarchyTableNode[]
  }

  const enterLocation = (location: HierarchyTableNode) => {
    if (isKilnOrMillLevel(location.type)) {
      return
    }

    const current = getCurrentLocations()
    if (Array.isArray(current)) {
      setLocationSiblings(current.map(({id, name}) => ({id, name})))
    }

    setLocationHistory((prev) => [...prev, {id: location.id, name: location.name}])
  }

  const goToLocation = (id: string) => {
    if (id === '') {
      setLocationHistory([])
      setLocationSiblings([])
      return
    }

    const result = resolveNavigationFromHistory(rootNodes, locationHistory, id)

    if (result) {
      setLocationHistory(result.newHistory)
      setLocationSiblings(result.siblings)
      return
    }

    const path = findPathToNode(rootNodes, id)
    if (path) {
      setLocationHistory(path)

      const parentPath = path.slice(0, -1)
      const current = parentPath.reduce<HierarchyTableNode | HierarchyTableNode[]>(
        (current, historyItem) => {
          if (!current) return rootNodes
          if (Array.isArray(current)) {
            return current.find((node) => node.id === historyItem.id)?.subItems || []
          }
          return current.subItems?.find((node) => node.id === historyItem.id)?.subItems || []
        },
        rootNodes
      )

      if (Array.isArray(current)) {
        setLocationSiblings(current.map(({id, name}) => ({id, name})))
      } else {
        setLocationSiblings([])
      }
    } else {
      setLocationHistory([])
      setLocationSiblings([])
    }
  }

  return {
    currentLocations: getCurrentLocations(),
    locationHistory,
    enterLocation,
    goToLocation,
    locationSiblings
  }
}
